import { sql } from "drizzle-orm";

type InviteDb =
  | {
      execute: (query: unknown) => Promise<unknown>;
    }
  | null
  | undefined;

type InviteRow = {
  code?: string;
  max_uses?: number | string;
  used_count?: number | string;
};
type InviteModeEnv = {
  INVITE_ONLY?: string;
  IS_PLATFORM?: string;
};

export type InviteReservationResult =
  | { ok: true; code: string; remaining: number }
  | { ok: false; error: string };

export function isInviteOnlyMode(env?: InviteModeEnv): boolean {
  const val = env ? env.INVITE_ONLY : process.env.INVITE_ONLY;
  if (val !== undefined && val !== "") return val !== "false" && val !== "0";
  return false;
}

function rowsFromResult(result: unknown): InviteRow[] {
  if (Array.isArray(result)) return result as InviteRow[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: InviteRow[] }).rows;
  }
  return [];
}

function normalizeInviteCode(code: unknown): string | null {
  if (typeof code !== "string") return null;
  const normalized = code.trim().toUpperCase();
  return normalized.length > 0 ? normalized : null;
}

function remainingFromRow(row: InviteRow): number {
  return Math.max(0, Number(row.max_uses ?? 1) - Number(row.used_count ?? 0));
}

export async function reserveInviteCode(db: InviteDb, code: unknown): Promise<InviteReservationResult> {
  const normalizedCode = normalizeInviteCode(code);
  if (!normalizedCode) return { ok: false, error: "초대 코드를 입력해주세요." };
  if (!db) return { ok: false, error: "초대 코드 확인이 불가능합니다." };

  try {
    const consumed = await db.execute(sql`
      UPDATE invite_codes
      SET used_count = used_count + 1
      WHERE UPPER(code) = UPPER(${normalizedCode})
        AND used_count < max_uses
      RETURNING code, max_uses, used_count
    `);
    const consumedRow = rowsFromResult(consumed)[0];
    if (consumedRow) {
      return {
        ok: true,
        code: String(consumedRow.code ?? normalizedCode).toUpperCase(),
        remaining: remainingFromRow(consumedRow),
      };
    }

    const checked = await db.execute(sql`
      SELECT code, max_uses, used_count
      FROM invite_codes
      WHERE UPPER(code) = UPPER(${normalizedCode})
      LIMIT 1
    `);
    const checkedRow = rowsFromResult(checked)[0];
    if (!checkedRow) return { ok: false, error: "유효하지 않은 초대 코드입니다." };
    if (remainingFromRow(checkedRow) <= 0) {
      return { ok: false, error: "이 초대 코드는 사용 횟수를 초과했습니다." };
    }
    return { ok: false, error: "초대 코드를 사용할 수 없습니다." };
  } catch (error) {
    console.error("[auth] invite code reservation error:", error);
    return { ok: false, error: "초대 코드 확인이 불가능합니다." };
  }
}

export async function releaseInviteCode(db: InviteDb, code: unknown): Promise<void> {
  const normalizedCode = normalizeInviteCode(code);
  if (!normalizedCode || !db) return;

  try {
    await db.execute(sql`
      UPDATE invite_codes
      SET used_count = GREATEST(used_count - 1, 0)
      WHERE UPPER(code) = UPPER(${normalizedCode})
    `);
  } catch (error) {
    console.error("[auth] invite code release error:", error);
  }
}
